import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import {
  Button,
  Input,
  Label,
  Modal,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@atrvd/saas-ui'
import { useFacilities, useUpdateBankAccount } from '@/hooks/useBanking'
import type { BankAccountRow } from '@/types/banking'
import {
  ACCOUNT_TYPE_CHOICES,
  accountTypeOptions,
} from '@/components/ledgersync/connectionManagement.utils'
import { accountFormValues, manualAccountSchema, type ManualAccountFormValues } from './manualBankSchema'
import { MANUAL_BANK_COPY, editAccountSubtitle } from './manualBankCopy'
import { RequiredMark } from './RequiredMark'
import {
  facilityOptionsFor,
  isAccountType,
} from './manualBanks.utils'

interface EditManualAccountDialogProps {
  account: BankAccountRow
  /** The bank's display name, for the subtitle. */
  bankName: string
  onClose: () => void
  /** Called with the saved values once the update landed, before the dialog closes. */
  onSaved?: (values: ManualAccountFormValues) => void
}

const FORM_ID = 'edit-manual-account-form'

/**
 * Edit one manual account or card: name, number, type and facility.
 *
 * Mount it only while open; the form is seeded from `account` once. The type
 * picker offers the standard five plus the account's own legacy type (e.g.
 * Line of Credit) so a save that does not touch Type keeps it; the facility
 * picker offers active facilities plus the account's own inactive one.
 */
export function EditManualAccountDialog({
  account,
  bankName,
  onClose,
  onSaved,
}: EditManualAccountDialogProps) {
  const updateAccount = useUpdateBankAccount()
  const facilitiesQuery = useFacilities({ activeOnly: false })

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ManualAccountFormValues>({
    resolver: zodResolver(manualAccountSchema),
    defaultValues: accountFormValues(account),
  })

  const typeOptions = isAccountType(account.account_type)
    ? accountTypeOptions(account.account_type)
    : ACCOUNT_TYPE_CHOICES
  const facilityOptions = facilityOptionsFor(facilitiesQuery.data ?? [], account.facility_id)

  const submit = handleSubmit(async (values) => {
    try {
      await updateAccount.mutateAsync({ id: account.id, data: values })
      toast.success(`"${values.name}" updated`)
      onSaved?.(values)
      onClose()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update the account')
    }
  })

  const id = (field: string) => `${FORM_ID}-${field}`
  const describedBy = (field: string, hasError: boolean) => (hasError ? `${id(field)}-error` : undefined)

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!updateAccount.isPending) onClose()
      }}
      title={MANUAL_BANK_COPY.editAccountTitle}
      description={editAccountSubtitle(bankName)}
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose} disabled={updateAccount.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            // Not for validity: an invalid save is refused with the fields highlighted.
            disabled={updateAccount.isPending || !facilitiesQuery.data}
          >
            {updateAccount.isPending && (
              <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
            )}
            Save changes
          </Button>
        </div>
      }
    >
      <form id={FORM_ID} onSubmit={submit} className="space-y-4" noValidate>
        <div className="space-y-2">
          <Label htmlFor={id('name')}>
            {MANUAL_BANK_COPY.editAccountNameLabel}
            <RequiredMark />
          </Label>
          <Input
            id={id('name')}
            placeholder={MANUAL_BANK_COPY.editAccountNamePlaceholder}
            aria-required="true"
            className="aria-[invalid=true]:border-destructive"
            aria-invalid={!!errors.name}
            aria-describedby={describedBy('name', !!errors.name)}
            {...register('name')}
          />
          {errors.name && (
            <p id={`${id('name')}-error`} className="text-sm text-destructive">
              {errors.name.message}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor={id('number')}>
              {MANUAL_BANK_COPY.editAccountNumberLabel}
              <RequiredMark />
            </Label>
            <Input
              id={id('number')}
              placeholder={MANUAL_BANK_COPY.numberPlaceholder}
              aria-required="true"
              className="aria-[invalid=true]:border-destructive"
              aria-invalid={!!errors.account_number}
              aria-describedby={describedBy('number', !!errors.account_number)}
              {...register('account_number')}
            />
            {errors.account_number && (
              <p id={`${id('number')}-error`} className="text-sm text-destructive">
                {errors.account_number.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor={id('type')}>
              {MANUAL_BANK_COPY.typeLabel}
              <RequiredMark />
            </Label>
            <Controller
              control={control}
              name="account_type"
              render={({ field }) => (
                <Select value={field.value ?? ''} onValueChange={field.onChange}>
                  <SelectTrigger
                    // Lets RHF focus (and so scroll to) this select when it is the
                    // first invalid field on submit, as it does for inputs.
                    ref={field.ref}
                    id={id('type')}
                    className="aria-[invalid=true]:border-destructive"
                    aria-invalid={!!errors.account_type}
                    aria-describedby={describedBy('type', !!errors.account_type)}
                  >
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {typeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.account_type && (
              <p id={`${id('type')}-error`} className="text-sm text-destructive">
                {errors.account_type.message}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor={id('facility')}>
            {MANUAL_BANK_COPY.facilityLabel}
            <RequiredMark />
          </Label>
          {facilitiesQuery.error ? (
            // Without the list the picker would offer nothing and read as "no facilities".
            <p role="alert" className="text-sm text-destructive">
              {facilitiesQuery.error.message}
            </p>
          ) : facilitiesQuery.isLoading ? (
            <div className="flex h-10 items-center">
              <Loader2 aria-label="Loading facilities" className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : facilityOptions.length === 0 ? (
            // No active facility and none of its own: an empty picker would
            // leave "Facility is required" with no way out and no reason given.
            <p role="alert" className="text-sm text-destructive">
              {MANUAL_BANK_COPY.noActiveFacilities}
            </p>
          ) : (
            <Controller
              control={control}
              name="facility_id"
              render={({ field }) => (
                <Select value={field.value ?? ''} onValueChange={field.onChange}>
                  <SelectTrigger
                    // Lets RHF focus (and so scroll to) this select when it is the
                    // first invalid field on submit, as it does for inputs.
                    ref={field.ref}
                    id={id('facility')}
                    className="aria-[invalid=true]:border-destructive"
                    aria-invalid={!!errors.facility_id}
                    aria-describedby={describedBy('facility', !!errors.facility_id)}
                  >
                    <SelectValue placeholder={MANUAL_BANK_COPY.facilityPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {facilityOptions.map((facility) => (
                      <SelectItem key={facility.id} value={facility.id}>
                        {facility.name}
                        {facility.code && ` (${facility.code})`}
                        {facility.inactive && ' — inactive'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          )}
          {errors.facility_id && (
            <p id={`${id('facility')}-error`} className="text-sm text-destructive">
              {errors.facility_id.message}
            </p>
          )}
        </div>
      </form>
    </Modal>
  )
}
